import type {
  ContactOrderCommunicationSelection,
  ContactOrderCreatePayload,
} from "../../../../../api/kehrblatt/contact-orders"
import type {
  ContactOrderAppointmentRow,
  RowCommunicationSelection,
} from "../planning-selection-contract"
import {
  communicationSelectionForPlanningRow,
  hasExplicitCommunicationSelection,
} from "../selection-state"

const isConfirmationInformationRow = (row: ContactOrderAppointmentRow) =>
  String(row.customerInformationMode ?? "").startsWith("confirmation")

const toPayloadSelection = (
  selection: RowCommunicationSelection,
): ContactOrderCommunicationSelection => ({
  digital: Object.entries(selection.digitalByCustomerId)
    .filter(([, channels]) => channels.length)
    .map(([customer_id, channels]) => ({ customer_id, channels })),
  postal: selection.postal,
})

const hasCommunication = (selection: RowCommunicationSelection) =>
  Object.values(selection.digitalByCustomerId).some((channels) => channels.length) ||
  selection.postal.mode !== "DISABLED"

export type BuildContactOrderBatchPayloadsInput = {
  targetEvents: ScheduleEvent[]
  appointmentRows: ContactOrderAppointmentRow[]
  caseIdByOrderId: Record<string, string | null>
  communicationSelectionByEventId: Record<string, RowCommunicationSelection>
  allowedContactChannels: readonly string[]
  includeInactiveRows?: boolean
  confirmPreviewSelection?: boolean
}

export const buildContactOrderBatchPayloads = ({
  targetEvents,
  appointmentRows,
  caseIdByOrderId,
  communicationSelectionByEventId,
  allowedContactChannels,
  includeInactiveRows = false,
  confirmPreviewSelection = false,
}: BuildContactOrderBatchPayloadsInput): Array<
  ContactOrderCreatePayload & { case_id?: string | null }
> => {
  const rowByEventId = new Map(appointmentRows.map((row) => [row.id, row]))
  const selectionState = { communicationSelectionByEventId }

  return targetEvents.flatMap((event) => {
    const row = rowByEventId.get(event.id)

    if (!row) return []

    const confirmationInformation = isConfirmationInformationRow(row)
    const explicitSelection = hasExplicitCommunicationSelection(row, selectionState)
    const selection = confirmationInformation
      ? ({
          digitalByCustomerId: {},
          postal: { mode: "DISABLED" },
        } as const)
      : communicationSelectionForPlanningRow(row, selectionState, allowedContactChannels)

    if (
      !confirmationInformation &&
      explicitSelection &&
      !hasCommunication(selection) &&
      !includeInactiveRows
    ) {
      return []
    }

    const payload: ContactOrderCreatePayload & { case_id?: string | null } = {
      work_order_id: event.work_order_id!,
      case_id: caseIdByOrderId[event.work_order_id!] ?? null,
      schedule_event_id: event.id,
      replace_existing: false,
      assigned_employee_ids: event.employee_id ? [event.employee_id] : [],
      proposed_start_at: event.start_at,
      proposed_end_at: event.end_at,
    }

    if (
      confirmationInformation ||
      explicitSelection ||
      (confirmPreviewSelection && row.planningPreview)
    ) {
      payload.communication_selection = toPayloadSelection(selection)
    }

    return payload
  })
}
